/**
 * The pro ladder's arithmetic — 99 price rows, and where an account's own
 * orders land on them.
 *
 * The ladder is a single **UP-normalised** book, which is what the venue keeps
 * (`docs/04` §7.8): one row per legal price, 1¢–99¢, with `My Bid` meaning a
 * resting buy of UP and `My Ask` a resting sell of UP. Everything a user can
 * express on it is one of those two, because `SELL UP @ 52` and `BUY DOWN @ 48`
 * are the same order and the engine already normalises them onto one ladder.
 *
 * ## Why `ownBookFrom` is a tested pure function and not four lines inline
 *
 * The four order shapes do **not** map onto the ladder one-to-one:
 *
 * | The venue's order | The ladder cell |
 * |---|---|
 * | `BUY UP @ p` | bid at `p` |
 * | `SELL UP @ p` | ask at `p` |
 * | `BUY DOWN @ q` | **ask at `100 − q`** |
 * | `SELL DOWN @ q` | **bid at `100 − q`** |
 *
 * The bottom two are not hypothetical and they are not rare. `splitSell`
 * rewrites an uncovered sell into a `BUY DOWN @ 100−p` *before it is signed*
 * (invariant 21, `domain/sell-shape.ts`), and the venue echoes a DOWN buy back
 * — deliberately, because after the rewrite the user genuinely placed one and
 * relabelling it needs client-held intent that does not survive a reload.
 *
 * So on a naive mapping an uncovered ask **disappears from the ladder the
 * instant it is acknowledged**: the optimistic row shows it in `My Ask`, the
 * receipt arrives, and it vanishes into a DOWN column that does not exist. That
 * is the single worst failure this feature can have — the user's own resting
 * order is invisible on the surface whose whole job is showing it — and it is
 * one `100 −` away in either direction.
 *
 * ## What a cell is worth
 *
 * `micro` is what a click can cancel and `pendingMicro` is what it cannot: a
 * submitted order with no receipt has no `order_id` yet, so there is nothing to
 * pass `DELETE /orders/{id}`. They are separate fields rather than one sum
 * because the ladder renders the difference and the click handler acts on it.
 *
 * Sizes are `remaining_size` — what is still resting — not `size`. An order
 * half filled is half a cell.
 *
 * No React, no DOM, no clock (invariant 10). Integer cents and `bigint`
 * micro-shares throughout; no float touches a price or a size (invariant 6).
 */

import { parseFixed, SHARES_DP } from '@/lib/decimal'
import type { Outcome } from '@/lib/decimal'
import type { PendingOrder } from '@/stores/pending-orders'

/** The lowest and highest prices the venue accepts, in integer cents. */
export const MIN_CENTS = 1
export const MAX_CENTS = 99

/**
 * The middle of the ladder, and the only row the viewport is ever put on.
 *
 * **A constant, deliberately, and not the touch.** Centring on the touch makes
 * the scroll position a function of the book, which means every book that
 * arrives is a chance for the panel to move — and it did, on every sixty-second
 * roll. 50¢ cannot be moved by anything the venue does, so "centre once on
 * load" is a statement the implementation can actually keep.
 */
export const CENTRE_CENTS = Math.round((MIN_CENTS + MAX_CENTS) / 2)

/**
 * The statuses that put an order on the book.
 *
 * **One definition, three readers.** This was declared twice before the pro
 * ladder — `RESTING` in `<OpenOrdersTable>` and `OPEN_STATUSES` in
 * `<OrderTicket>` — and a third copy here would have made the ladder and the
 * orders table able to disagree about whether an order exists, which is exactly
 * the disagreement a user would read as the ladder being broken.
 */
export const RESTING_STATUSES: ReadonlySet<string> = new Set([
  'PENDING_NEW',
  'OPEN',
  'PARTIALLY_FILLED',
])

/**
 * Every row of the ladder, highest price first.
 *
 * Descending because that is what a ladder is: expensive at the top, cheap at
 * the bottom, asks above bids. Allocated once by the caller and never rebuilt —
 * the row set is fixed for the life of the panel, which is what lets a click
 * target stay still while the book moves under it.
 */
export function ladderRowPrices(): readonly number[] {
  const out: number[] = []
  for (let cents = MAX_CENTS; cents >= MIN_CENTS; cents--) out.push(cents)
  return out
}

/** The row index of a price, for a `ladderRowPrices()`-shaped array. */
export function rowIndexOf(cents: number): number {
  return MAX_CENTS - cents
}

/** What this account has resting at one price on one side of the ladder. */
export interface OwnLevel {
  /** Acknowledged and cancellable. */
  micro: bigint
  /** Submitted, no receipt yet — visible, and not cancellable. */
  pendingMicro: bigint
  /** Every order id at this price. A cancel click cancels all of them. */
  orderIds: string[]
  /**
   * What was placed at this price — every order's original `size`, plus what
   * is still pending — and how much of it has traded. Together they are the
   * standard book's "Filled 2 / 5" card; the pro ladder shows only `micro`.
   * `placedMicro − filledMicro` is `micro + pendingMicro` for any order whose
   * three sizes agree, which the venue's always do.
   */
  placedMicro: bigint
  filledMicro: bigint
}

/** This account's own orders, projected onto the UP ladder. */
export interface OwnBook {
  bid: Map<number, OwnLevel>
  ask: Map<number, OwnLevel>
}

/** The side of the ladder a cell belongs to. `bid` is `My Bid`, `ask` is `My Ask`. */
export type LadderSide = 'bid' | 'ask'

/**
 * Which side of the ladder an order sits on, and at what price.
 *
 * The whole of the mapping table in this file's header, in one place. `null` for
 * anything unrecognisable rather than a guess — a row with a missing outcome is
 * a row that should not paint, not one that should paint somewhere plausible.
 */
export function ladderCellOf(
  outcome: unknown,
  side: unknown,
  priceCents: unknown,
): { side: LadderSide; cents: number } | null {
  if (typeof priceCents !== 'number' || !Number.isInteger(priceCents)) return null
  if (outcome !== 'UP' && outcome !== 'DOWN') return null
  if (side !== 'BUY' && side !== 'SELL') return null

  // A DOWN order is the same order seen from the other end: its price becomes
  // `100 − q` and its side flips, which is exactly the engine's own
  // normalisation (`docs/04` §7.8) and exactly `complementPriceCents`.
  const cents = outcome === 'UP' ? priceCents : MAX_CENTS + MIN_CENTS - priceCents
  if (cents < MIN_CENTS || cents > MAX_CENTS) return null

  const buysUp = (outcome === 'UP') === (side === 'BUY')
  return { side: buysUp ? 'bid' : 'ask', cents }
}

/**
 * Project this account's resting and pending orders onto the ladder.
 *
 * Orders arrive as untyped records — there is no `Order` interface on this side
 * of the wire — so every field is read defensively and an unreadable row is
 * skipped rather than defaulted.
 *
 * Cost is one pass over the account's open orders per `orders` frame. That set
 * is bounded by `config.limits`' open-order caps, so it is tens of rows, not
 * thousands; there is no index to maintain and nothing to invalidate.
 */
export function ownBookFrom(
  orders: readonly Record<string, unknown>[],
  pending: readonly PendingOrder[],
  marketId: string | null,
): OwnBook {
  const book: OwnBook = { bid: new Map(), ask: new Map() }
  if (!marketId) return book

  for (const order of orders) {
    if (order.market_id !== marketId) continue
    if (!RESTING_STATUSES.has(String(order.status))) continue
    const cell = ladderCellOf(order.outcome, order.side, order.price_cents)
    if (!cell) continue
    // `remaining_size` — what is still on the book. `size` is what was placed,
    // and a half-filled order that kept rendering its original size would tell
    // the user they can cancel shares that have already traded.
    const micro = sharesOf(order.remaining_size ?? order.size)
    if (micro <= 0n) continue
    const level = levelAt(book[cell.side], cell.cents)
    level.micro += micro
    const filled = sharesOf(order.filled_size)
    // `size` is what was placed. Rebuilt from the other two when absent rather
    // than read as zero, so a card never says "Filled 3 / 0".
    const placed = sharesOf(order.size)
    level.filledMicro += filled
    level.placedMicro += placed > 0n ? placed : micro + filled
    const id = order.order_id
    if (typeof id === 'string' && id !== '') level.orderIds.push(id)
  }

  for (const row of pending) {
    if (row.marketId !== marketId) continue
    // A market order never rests, so it is never a cell. Its optimistic row
    // exists for the orders table, which shows it going through.
    if (row.orderType !== 'LIMIT') continue
    const cell = ladderCellOf(row.outcome, row.side, row.priceCents)
    if (!cell) continue
    const micro = sharesOf(row.size)
    if (micro <= 0n) continue
    const level = levelAt(book[cell.side], cell.cents)
    level.pendingMicro += micro
    level.placedMicro += micro
  }

  return book
}

/**
 * The account's own level at one row of a book shown in either outcome's terms.
 *
 * `OwnBook` is UP-normalised, which is the pro ladder's only view. The standard
 * book also shows the DOWN ladder — the mirror `BookStore.downLadder()`
 * computes — and on it a row is the same interest seen from the other end: a
 * DOWN bid at `q` is an UP ask at `100 − q`, and a DOWN ask a UP bid. This is
 * that mapping, once, so the mark on a DOWN row and the orders a click on it
 * cancels can never disagree about which rung they mean.
 */
export function ownLevelFor(
  book: OwnBook,
  outcome: Outcome,
  side: LadderSide,
  cents: number,
): OwnLevel | undefined {
  const rung = upRungOf(outcome, side, cents)
  return book[rung.side].get(rung.cents)
}

/** A row of a book shown in `outcome`'s terms, as the UP rung it is. */
export function upRungOf(
  outcome: Outcome,
  side: LadderSide,
  cents: number,
): { side: LadderSide; cents: number } {
  if (outcome === 'UP') return { side, cents }
  return { side: side === 'bid' ? 'ask' : 'bid', cents: MAX_CENTS + MIN_CENTS - cents }
}

/** Where a price sits relative to the touch. */
export type Zone = 'bid' | 'spread' | 'ask'

/**
 * The zone a row is in, for the tint that replaces a mid row.
 *
 * **There is no mid row.** A divider that moved with the spread would shift
 * every row below it, and the ladder's fixed geometry is the thing that lets a
 * user aim at a price rather than at a position. The spread is a *tint across
 * rows that already exist* instead, so nothing moves when it widens.
 *
 * The touch rows themselves are `bid` and `ask` — they carry liquidity, so they
 * belong to their side. `spread` is strictly between them, which on a
 * one-cent-wide market is the empty set.
 */
export function zoneFor(cents: number, bestBid: number | null, bestAsk: number | null): Zone {
  if (bestBid !== null && cents <= bestBid) return 'bid'
  if (bestAsk !== null && cents >= bestAsk) return 'ask'
  // Everything else, which on a one-sided or empty book is most of the ladder.
  // It is not a hole: an order rests perfectly well at a price nobody is
  // quoting, and on a market this young that is the normal case rather than the
  // exception.
  return 'spread'
}

// ---------------------------------------------------------------------------

function levelAt(side: Map<number, OwnLevel>, cents: number): OwnLevel {
  let level = side.get(cents)
  if (!level) {
    level = { micro: 0n, pendingMicro: 0n, orderIds: [], placedMicro: 0n, filledMicro: 0n }
    side.set(cents, level)
  }
  return level
}

/**
 * A 6dp share string as exact micro-shares.
 *
 * Zero on anything unparseable, which keeps one malformed row from emptying the
 * whole column. The frame it came in on is one the checksum will reject a
 * moment later anyway.
 */
function sharesOf(value: unknown): bigint {
  if (typeof value !== 'string') return 0n
  try {
    return parseFixed(value, SHARES_DP)
  } catch {
    return 0n
  }
}

/** `UP` is the ladder's own outcome; the type is re-exported for its readers. */
export type { Outcome }
